/**
 * Has the reviewer picked enough 1st Choices to leave the Compare & Select step?
 *
 * ⚠️ ONE READER WAS LEFT ON THE OLD SELECTION SHAPE AND THE BUTTON NEVER ENABLED.
 *
 * Selections used to be written WITHOUT a siteId in shared mode — "the entry with no site" was
 * how the one tender-wide pick was recognised. handleSelectRank was moved off that convention:
 * every entry now carries the siteId of the card that was clicked, and shared mode owns one
 * rank slot for the whole tender, matched on rank alone (see inSelectionScope).
 *
 * The readiness check was not moved with it. It still asked for `rank === 1 && !s.siteId`, a
 * combination the current writer can never produce, so on a shared-ABN tender "Continue to
 * Recommended Offer" stayed disabled however many offers were picked.
 *
 * Seen on prod tender 23947ac5 (GIN GIN AG PTY LTD): two sites, both ABN 41675307507, so
 * shared mode; ranks 1, 2 and 3 all stored, every one of them carrying
 * siteId a08d88b6. Stage 6 was unreachable.
 *
 * ⚠️ STRICTLY MORE PERMISSIVE, AND ONLY IN SHARED MODE. A legacy entry with no siteId passed
 * before and passes now; an entry with one was refused and is now accepted. Nothing that
 * enabled the button before can stop enabling it.
 */
export const hasRequiredRankOneSelections = (input: {
  /** One legal entity across every site: one set of rank slots for the whole tender. */
  allSitesSameAbn: boolean;
  selections: ReadonlyArray<{ rank: number; siteId?: string }>;
  /** Only the sites that actually carry offers — a site with none cannot be ranked. */
  siteIdsWithOffers: ReadonlyArray<string>;
}): boolean => {
  const { allSitesSameAbn, selections, siteIdsWithOffers } = input;

  // Shared: rank alone, exactly as handleSelectRank and inSelectionScope read it.
  if (allSitesSameAbn) return selections.some(s => s.rank === 1);

  // Per-site: each site has its own rank slots, so each needs its own 1st Choice.
  if (siteIdsWithOffers.length === 0) return false;
  return siteIdsWithOffers.every(siteId =>
    selections.some(s => s.rank === 1 && s.siteId === siteId),
  );
};
